import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/client';
import RaceResultsViewer from '../components/RaceResultsViewer';
import { DriverDetail, DriverSession, UserProfile } from '../types';
import { SortDirection, getSortIndicator, sortRows } from '../utils/sort';
import './DriverDetailPage.css';

interface DriverDetailPageProps {
  driverId: string;
  onBack: () => void;
}

interface SessionGroup {
  // A group is either a single standalone session or multiple race-weekend sessions
  isGroup: boolean;
  raceId: string | null;
  // For standalone sessions, `sessions` has one entry
  sessions: DriverSession[];
  // Representative fields for display/sorting
  serverName: string;
  trackName: string;
  trackLayoutName: string;
  carModel: string;
  bestLapMs: number | null;
  sessionDate: string;
}

const sessionTypeSuffix = (sessionType: string) => {
  const type = sessionType.toLowerCase();
  if (type.includes('practice')) return 'P';
  if (type.includes('qual')) return 'Q';
  if (type.includes('warmup') || type.includes('warm-up')) return 'W';
  if (type === 'race' || type.includes('race')) return 'R';
  return sessionType.charAt(0).toUpperCase();
};

const formatTimeFromMs = (ms: number | null): string => {
  if (ms === null) return '—';
  const totalSeconds = Math.floor(ms / 1000);
  const milliseconds = ms % 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0) {
    return `${minutes}:${String(seconds).padStart(2, '0')}.${String(milliseconds).padStart(3, '0')}`;
  }

  return `${seconds}.${String(milliseconds).padStart(3, '0')}`;
};

const isRaceWeekend = (session: DriverSession): boolean => {
  // Race weekend sessions have a non-null raceId and their sessionName contains "Race Weekend"
  if (!session.raceId) return false;
  const name = (session.sessionName || '').toLowerCase();
  return name.includes('race weekend');
};

const renderSocialIcon = (platform: 'twitch' | 'youtube') => {
  if (platform === 'twitch') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="social-icon">
        <path d="M6.5 3 4 5.5V18.5H8V21L10.5 18.5H13.5L20 12V3H6.5Zm11 7.5-2.5 2.5H12L10.3 7.5H8.5V15H17.5V10.5Z" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="social-icon">
      <path d="M21.6 7.2a2.8 2.8 0 0 0-2-2C17.9 4.5 12 4.5 12 4.5s-5.9 0-7.6.7a2.8 2.8 0 0 0-2 2C2 8.9 2 12 2 12s0 3.1.4 4.8a2.8 2.8 0 0 0 2 2c1.7.7 7.6.7 7.6.7s5.9 0 7.6-.7a2.8 2.8 0 0 0 2-2c.4-1.7.4-4.8.4-4.8s0-3.1-.4-4.8ZM10 15.5v-7l6 3.5-6 3.5Z" />
    </svg>
  );
};

/**
 * Condense a server name like "SSRI - S01R03 - Monza - F4 Championship - https://..."
 * down to "S01R03 - F4 Championship" for display.
 * Strips: leading abbreviation (all-caps/digits word before first " - "),
 *         the track segment (any part that matches the track name),
 *         and any trailing URL.
 */
const condenseServerName = (serverName: string, trackName?: string): string => {
  // Remove any URL-like token (with or without protocol) — e.g. "https://foo.com" or "foo.bar.com"
  let name = serverName
    .replace(/https?:\/\/[^\s\-]+/g, '')
    .replace(/(?<!\w)\b\w+\.\w{2,}(?:\/\S*)?\b/g, '')
    .trim()
    .replace(/(\s*-)+\s*$/, '')
    .trim();

  const parts = name.split(/\s*-\s*/);
  if (parts.length < 2) return name;

  // Drop the first segment if it looks like a short abbreviation (≤6 chars, no spaces)
  let start = 0;
  if (parts[0].length <= 6 && !/\s/.test(parts[0])) start = 1;

  // Find which remaining part is the race code (e.g. S01R03, R03, etc.)
  const raceCodeIdx = parts.findIndex((p, i) => i >= start && /^[SR]\d/i.test(p.trim()));

  // Find which part matches the track (to skip it)
  const trackLower = (trackName || '').toLowerCase();
  const isTrackPart = (p: string) =>
    trackLower.length > 3 && p.toLowerCase().includes(trackLower.substring(0, Math.min(8, trackLower.length)));

  if (raceCodeIdx !== -1) {
    // Keep race code + everything after except track parts
    const kept = parts
      .slice(raceCodeIdx)
      .filter((p) => !isTrackPart(p))
      .join(' - ')
      .trim();
    return kept || name;
  }

  // Fallback: drop abbreviation prefix only
  return parts.slice(start).filter((p) => !isTrackPart(p)).join(' - ').trim() || name;
};

const SESSION_TYPE_ORDER: Record<string, number> = { practice: 0, qualify: 1, warmup: 2, race: 3 };

const sessionTypeSortOrder = (sessionType: string): number =>
  SESSION_TYPE_ORDER[sessionType.toLowerCase()] ?? 99;

const DriverDetailPage: React.FC<DriverDetailPageProps> = ({ driverId, onBack }) => {
  const [driver, setDriver] = useState<DriverDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [viewingSession, setViewingSession] = useState<{ sessionId: number; carModel: string } | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: 'session' | 'track' | 'car' | 'bestLap' | 'date'; direction: SortDirection }>({ key: 'date', direction: 'desc' });

  // Group sessions: race-weekend sessions with the same raceId → one row; others → individual rows
  const sessionGroups = useMemo((): SessionGroup[] => {
    if (!driver?.sessions) return [];

    const raceWeekendMap = new Map<string, DriverSession[]>();
    const standalone: DriverSession[] = [];

    for (const session of driver.sessions) {
      if (isRaceWeekend(session) && session.raceId) {
        const existing = raceWeekendMap.get(session.raceId) || [];
        existing.push(session);
        raceWeekendMap.set(session.raceId, existing);
      } else {
        standalone.push(session);
      }
    }

    const groups: SessionGroup[] = [];

    // Race weekend groups
    for (const [raceId, sessions] of raceWeekendMap) {
      // Sort sessions within the group by session type order
      const sorted = [...sessions].sort((a, b) => sessionTypeSortOrder(a.sessionType) - sessionTypeSortOrder(b.sessionType));
      const raceSession = sorted.find((s) => s.sessionType.toLowerCase() === 'race') || sorted[0];
      groups.push({
        isGroup: true,
        raceId,
        sessions: sorted,
        serverName: raceSession.serverName,
        trackName: raceSession.trackName,
        trackLayoutName: raceSession.trackLayoutName,
        carModel: raceSession.carModel,
        bestLapMs: raceSession.bestLapMs,
        sessionDate: raceSession.sessionDate,
      });
    }

    // Standalone sessions
    for (const session of standalone) {
      groups.push({
        isGroup: false,
        raceId: null,
        sessions: [session],
        serverName: session.serverName,
        trackName: session.trackName,
        trackLayoutName: session.trackLayoutName,
        carModel: session.carModel,
        bestLapMs: session.bestLapMs,
        sessionDate: session.sessionDate,
      });
    }

    return groups;
  }, [driver?.sessions]);

  const sortedGroups = useMemo(() => {
    // For best lap: null means no valid lap, always sort last regardless of direction
    const nullLapSentinel = sortConfig.direction === 'asc' ? Infinity : -1;
    return sortRows(sessionGroups, sortConfig.direction, (group) => {
      switch (sortConfig.key) {
        case 'session':
          return group.serverName;
        case 'track':
          return `${group.trackName} ${group.trackLayoutName}`.trim();
        case 'car':
          return group.carModel;
        case 'bestLap':
          return group.bestLapMs ?? nullLapSentinel;
        case 'date':
          return group.sessionDate ? new Date(group.sessionDate).getTime() : 0;
        default:
          return '';
      }
    });
  }, [sessionGroups, sortConfig]);

  const handleSort = (key: 'session' | 'track' | 'car' | 'bestLap' | 'date') => {
    setSortConfig((current) => {
      if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  useEffect(() => {
    const loadDriver = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.getDriver(driverId);
        setDriver(res.data.driver || null);
      } catch (err) {
        console.error('Failed to load driver details:', err);
        setError('Unable to load driver details.');
      } finally {
        setLoading(false);
      }
    };

    loadDriver();
  }, [driverId]);

  useEffect(() => {
    const loadProfile = async () => {
      if (!driver?.playerId) {
        setProfile(null);
        return;
      }

      try {
        setProfileLoading(true);
        const res = await api.getPublicProfileBySteamId(driver.playerId);
        setProfile(res.data.profile || null);
      } catch (err) {
        setProfile(null);
      } finally {
        setProfileLoading(false);
      }
    };

    loadProfile();
  }, [driver?.playerId]);

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <button className="small-button" onClick={onBack}>
            ← Back
          </button>
          <h1>
            {driver?.firstName && driver?.lastName
              ? `${driver.firstName} ${driver.lastName}`
              : driver?.driverName || 'Driver'}
          </h1>
          <p>Profile and historical session results.</p>
        </div>
      </div>

      {loading && <div className="empty-state">Loading driver profile...</div>}
      {error && <div className="empty-state">{error}</div>}

      {!loading && driver && (
        <div className="section-card" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: '180px 1fr' }}>
            <div className="card" style={{ padding: '18px', textAlign: 'center' }}>
              {profile?.avatarUrl ? (
                <img src={profile.avatarUrl} alt={profile.displayName} style={{ width: 120, height: 120, borderRadius: 24, objectFit: 'cover', margin: '0 auto 16px' }} />
              ) : (
                <div
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: 24,
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    margin: '0 auto 16px',
                  }}
                />
              )}
              <div>
                {driver.firstName && driver.lastName
                  ? `${driver.firstName} ${driver.lastName}`
                  : driver.driverName}
              </div>
              <div>{profile?.steamId ? profile?.displayName || 'No linked profile' : 'No linked steam account'}</div>
              <div>{driver.nation}</div>
            </div>
            <div>
              <p><strong>First seen:</strong> {driver.firstSeen ? new Date(driver.firstSeen).toLocaleDateString() : 'Unknown'}</p>
              <p><strong>Last seen:</strong> {driver.lastSeen ? new Date(driver.lastSeen).toLocaleDateString() : 'Unknown'}</p>
              <p><strong>Sessions:</strong> {driver.sessionCount}</p>
              {profile && (
                <div className="" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '16px' }}>
                  <button
                    className="small-button social-button"
                    disabled={!profile.twitch}
                    onClick={profile.twitch ? () => window.open(`https://twitch.tv/${profile?.twitch?.replace(/^@/, '')}`, '_blank', 'noopener,noreferrer') : undefined}
                  >
                    <span className="social-button-content">
                      {renderSocialIcon('twitch')}
                      <span>Twitch</span>
                    </span>
                  </button>
                  <button
                    className="small-button social-button"
                    disabled={!profile.youtube}
                    onClick={profile.youtube ? () => window.open(`https://www.youtube.com/@${profile?.youtube?.replace(/^@/, '')}`, '_blank', 'noopener,noreferrer') : undefined}
                  >
                    <span className="social-button-content">
                      {renderSocialIcon('youtube')}
                      <span>YouTube</span>
                    </span>
                  </button>
                </div>
              )}
              {profileLoading && <p style={{ marginTop: '16px' }}>Loading linked profile…</p>}
            </div>
          </div>
        </div>
      )}

      {!loading && driver && (
        <div className="section-card">
          <h2>Recent sessions</h2>
          <div className="driver-sessions-sort-bar">
            <span className="driver-sessions-sort-label">Sort by</span>
            <select
              className="driver-sessions-sort-select"
              value={`${sortConfig.key}:${sortConfig.direction}`}
              onChange={(e) => {
                const [k, d] = e.target.value.split(':') as ['session' | 'track' | 'car' | 'bestLap' | 'date', SortDirection];
                setSortConfig({ key: k, direction: d });
              }}
            >
              <option value="date:desc">Date (newest first)</option>
              <option value="date:asc">Date (oldest first)</option>
              <option value="track:asc">Track (A → Z)</option>
              <option value="track:desc">Track (Z → A)</option>
              <option value="car:asc">Car (A → Z)</option>
              <option value="car:desc">Car (Z → A)</option>
              <option value="bestLap:asc">Best Lap (fastest)</option>
              <option value="bestLap:desc">Best Lap (slowest)</option>
              <option value="session:asc">Session (A → Z)</option>
              <option value="session:desc">Session (Z → A)</option>
            </select>
          </div>
          <div className="table-wrapper">
            <table className="driver-sessions-table">
              <thead>
                <tr>
                  <th><button type="button" onClick={() => handleSort('session')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Session <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'session', sortConfig.direction)}</span></button></th>
                  <th><button type="button" onClick={() => handleSort('track')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Track <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'track', sortConfig.direction)}</span></button></th>
                  <th><button type="button" onClick={() => handleSort('car')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Car <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'car', sortConfig.direction)}</span></button></th>
                  <th><button type="button" onClick={() => handleSort('bestLap')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Best lap <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'bestLap', sortConfig.direction)}</span></button></th>
                  <th><button type="button" onClick={() => handleSort('date')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Date <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'date', sortConfig.direction)}</span></button></th>
                </tr>
              </thead>
              <tbody>
                {sortedGroups.map((group) => {
                  const s0 = group.sessions[0];
                  const key = group.isGroup
                    ? `group-${group.raceId}`
                    : `session-${s0.sessionId}-${s0.carModel}-${s0.filename}`;
                  return (
                    <tr
                      key={key}
                      style={{ cursor: group.isGroup ? 'default' : 'pointer' }}
                      onClick={group.isGroup ? undefined : () => setViewingSession({ sessionId: group.sessions[0].sessionId, carModel: group.sessions[0].carModel })}
                    >
                      <td data-label="Session">
                        {group.isGroup ? (
                          <span className="ds-session-group">
                            <span className="ds-session-name">
                              {condenseServerName(group.serverName, group.trackName)}
                            </span>
                            {/* Buttons visible on desktop only; on mobile they appear in td.ds-buttons-cell */}
                            <span className="ds-session-buttons ds-buttons-desktop">
                              {group.sessions.map((s) => (
                                <button
                                  key={s.sessionId}
                                  type="button"
                                  className="ds-session-type-btn"
                                  onClick={(e) => { e.stopPropagation(); setViewingSession({ sessionId: s.sessionId, carModel: s.carModel }); }}
                                  title={`View ${s.sessionType} results`}
                                >
                                  {sessionTypeSuffix(s.sessionType)}
                                </button>
                              ))}
                            </span>
                          </span>
                        ) : (
                          <span className="ds-session-name">
                            {condenseServerName(group.sessions[0].serverName || group.sessions[0].sessionName, group.trackName)}
                          </span>
                        )}
                      </td>
                      <td data-label="Track"><span>{group.trackName} {group.trackLayoutName}</span></td>
                      <td data-label="Car"><span>{group.carModel}</span></td>
                      <td data-label="Best lap"><span>{formatTimeFromMs(group.bestLapMs)}</span></td>
                      <td data-label="Date"><span>{group.sessionDate ? new Date(group.sessionDate).toLocaleDateString() : 'Unknown'}</span></td>
                      {/* Mobile-only cell: session-type buttons below the track name */}
                      <td className="ds-buttons-cell" aria-hidden="true">
                        {group.isGroup && (
                          <span className="ds-session-buttons">
                            {group.sessions.map((s) => (
                              <button
                                key={s.sessionId}
                                type="button"
                                className="ds-session-type-btn"
                                onClick={(e) => { e.stopPropagation(); setViewingSession({ sessionId: s.sessionId, carModel: s.carModel }); }}
                                title={`View ${s.sessionType} results`}
                              >
                                {sessionTypeSuffix(s.sessionType)}
                              </button>
                            ))}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {driver.sessions.length === 0 && (
                  <tr>
                    <td colSpan={6} className="empty-state">No sessions found for this driver.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {viewingSession !== null && (
        <RaceResultsViewer
          sessionId={viewingSession.sessionId}
          onClose={() => setViewingSession(null)}
          fetchResult={async () => api.getResultsSession(viewingSession.sessionId.toString())}
        />
      )}
    </div>
  );
};

export default DriverDetailPage;
